import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { first } from 'lodash';
import { map, Observable, throwError } from 'rxjs';
import { ChatDataService } from 'src/shared/services/chat.data.service/chat.data.service';
import { bookingexternal, trialmanagementexternal } from 'src/shared/services/client/client';
import { withChatService } from '../features/chat-service/chat-service.feature';

export const ChatDataStore = signalStore(
  withState({
    chats: [] as bookingexternal.GetChatResponse[],
    latestChat: null as bookingexternal.GetChatResponse | null,
    latestIncompleteChat: null as bookingexternal.GetChatResponse | null,
    incompleteChats: [] as bookingexternal.GetChatResponse[],
    awaitingPayment: undefined as bookingexternal.GetChatResponse | undefined,
    creating: false,
    loading: false,
    error: null as string | null,
  }),
  withChatService(),
  withMethods((store) => {
    const chatDataService = inject(ChatDataService);

    return {
      loadMyChats() {
        patchState(store, { loading: true, error: null });

        return chatDataService.ListMyChats().pipe(
          map((response) => ({
            chats: getAllValidChats(response.chats),
            latestChat: getLatestChat(response.chats),
            latestIncompleteChat: getLatestIncompleteChat(response.chats),
            incompleteChats: getAllIncompleteChats(response.chats),
            awaitingPayment: getLatestAwaitingPayment(response.chats),
          })),
          tapResponse({
            next: ({ chats, latestChat, latestIncompleteChat, incompleteChats, awaitingPayment }) => {
              patchState(store, {
                chats,
                latestChat,
                latestIncompleteChat,
                incompleteChats,
                awaitingPayment,
                loading: false,
              });
            },
            error: (err: any) => {
              patchState(store, { error: err.message, loading: false });
            },
          })
        );
      },

      startNewChat(params: Partial<bookingexternal.CreateChatRequest>): Observable<bookingexternal.CreateChatResponse> {
        if (store.creating()) {
          return throwError(() => new Error('A chat is already being created, please wait for it to complete'));
        }

        patchState(store, { creating: true, error: null });

        if (!params.chat_service_id) {
          params['chat_service_id'] = store.chatServiceDocId() || '';
        }

        return chatDataService.CreateChat(params as bookingexternal.CreateChatRequest).pipe(
          tapResponse({
            next: (chat: bookingexternal.CreateChatResponse) => {
              console.log(`Chat created: ${chat.doc_id}`);
            },
            error: (err: any) => {
              patchState(store, { error: err.message });
            },
          })
        );
      },
      startNewChatWithTrial(
        trial_doc_id: string,
        params: trialmanagementexternal.CreateTrialChatRequest
      ): Observable<trialmanagementexternal.CreateTrialChatResponse> {
        return chatDataService.CreateTrialChat(trial_doc_id, params);
      },
      resetCreating() {
        patchState(store, { creating: false });
      },

      setChatServiceDocId(chatServiceDocId: string | null) {
        patchState(store, { chatServiceDocId });
      },
      resetData() {
        patchState(store, {
          chats: [],
          latestChat: null,
          latestIncompleteChat: null,
          incompleteChats: [],
          awaitingPayment: undefined,
        });
      },
    };
  })
);

export enum ChatStatus {
  complete = 'complete',
  curious = 'curious',
  pending = 'pending',
  llmCallTriage = 'llm-call-triage',
  llmChatTriage = 'llm-chat-triage',
  llmDiscovery = 'llm-discovery',
  frozen = 'frozen',
  handover = 'handover',
  active = 'active',
  paymentRequired = 'payment_required',
  abandoned = 'abandoned',
  aIOutcomeAwaitingVetReview = 'ai-outcome-awaiting-vet-review',
  aIOutcomeVetReviewInProgress = 'ai-outcome-vet-review-in-progress',
  aIOutcomeAwaitingUser = 'ai-outcome-awaiting-user',
  aIOutcomeAwaitingVetChat = 'ai-outcome-awaiting-vet-chat',
  aIOutcomeVetChatInProgress = 'ai-outcome-vet-chat-in-progress',
  aIGeneralTriage = 'llm-triage-general',
  llmTriageGeneralDeferredChoice = 'llm-triage-general-deferred-choice',
}

function getAllValidChats(chats: bookingexternal.GetChatResponse[]) {
  return chats.filter((chat) => isChatValid(chat));
}

function getLatestIncompleteChat(
  chats: bookingexternal.GetChatResponse[]
): bookingexternal.GetChatResponse | undefined {
  return chats.find((chat) => isChatIncomplete(chat));
}

function getLatestChat(chats: bookingexternal.GetChatResponse[]): bookingexternal.GetChatResponse | undefined {
  return first(chats);
}

function getAllIncompleteChats(chats: bookingexternal.GetChatResponse[]) {
  return chats.filter((chat) => isChatIncomplete(chat));
}

function getLatestAwaitingPayment(chats: bookingexternal.GetChatResponse[]) {
  return chats.find((chat) => isChatAwaitingPayment(chat));
}

function isChatIncomplete(chat: bookingexternal.GetChatResponse): boolean {
  return (
    chat.status.toLowerCase() !== ChatStatus.complete &&
    chat.status.toLowerCase() !== ChatStatus.abandoned &&
    (chat.status !== ChatStatus.llmCallTriage || !chat?.status_history?.includes(ChatStatus.llmCallTriage)) &&
    (chat.status !== ChatStatus.llmDiscovery || !chat?.status_history?.includes(ChatStatus.llmDiscovery))
  );
}

function isChatValid(chat: bookingexternal.GetChatResponse): boolean {
  return chat.status !== ChatStatus.llmCallTriage || !chat?.status_history?.includes(ChatStatus.llmCallTriage);
}

function isChatAwaitingPayment(chat: bookingexternal.GetChatResponse): boolean {
  return chat.status === ChatStatus.paymentRequired && chat.payment_status === 'Unpaid';
}
